/**
 * Stretches of a counter's run no prop may enter, beyond what already stands
 * on it: the PREP ZONE, and anything too low under an upper cabinet or a hood
 * for the prop at hand. (Sinks and cooktops are voids, handled by
 * `surfaces.js` through `isKitchenVoid`.)
 *
 * All intervals are in the counter's own run frame, metres from its start.
 */

const {
  isBasinLike,
  runIntervalFor,
  depthOffset,
  planDims,
} = require('../surfaces');
const { objectTypeOf } = require('../../ownership');
const { stagingPropDimsM } = require('../../propDims');
const { footprintFor } = require('../../../geometry/objectFootprint');
const { objectLocalX } = require('../../../dorm-styling/plan/shared');
const { SURFACE_VOID_PAD_M } = require('../constants');
const {
  isCooktop,
  isCookingUnit,
  nearCounter,
  runPosition,
  planDistance,
  objectLocalZ,
} = require('./geometry');
const {
  HEADROOM_CLEARANCE_M,
  GROUP_SPACING_M,
  KITCHEN_GROUPING,
} = require('./program');

const IN = 0.0254;
/** NKBA: at least 36 in of continuous counter, 24 in deep, beside the sink. */
const PREP_AREA_M = 36 * IN;
/** A range this close to a slab's end stands at it (ranges sit in the gaps). */
const RANGE_ABUTS_M = 2 * IN;

/** What is a hole in a kitchen counter: a sink, or a cooktop sitting on it. */
const isKitchenVoid = (o) => isBasinLike(o) || isCooktop(o);

/**
 * The prep zone on one counter: the counter between its sink and the nearest
 * cooking unit, kept clear at full depth ("keep the prep area — where you
 * chop, mix, and cook — as clear as possible"). Three cases:
 *   - the cooking unit sits on this slab (a cooktop): sink rim to cooktop rim;
 *   - a range abuts this slab: sink rim to the slab end at the range;
 *   - the range is elsewhere (the other leg of an L, an island): the NKBA
 *     prep area — 36 in of counter beside the sink, on the side facing it.
 *     Not the whole rest of the slab: on 50 Park St that reserved 76 in and
 *     left everything left of the sink bare while the props crowded the right.
 * No sink on this counter, or no cooking unit in the kitchen: no prep zone.
 */
function prepZone(counter, kitchen) {
  const sinkRuns = kitchen.sinks
    .map((s) => ({ s, iv: runIntervalFor(counter.host, s, counter.hostW) }))
    .filter((x) => x.iv && nearCounter(counter, x.s, 0.02));
  if (!sinkRuns.length) return [];
  const cooking = [...kitchen.cooktops, ...kitchen.ranges].filter(
    isCookingUnit
  );
  if (!cooking.length) return [];
  const out = [];
  for (const { s, iv } of sinkRuns) {
    const unit = cooking.reduce((best, c) =>
      planDistance(c.center, s.center) < planDistance(best.center, s.center)
        ? c
        : best
    );
    const onSlab = runIntervalFor(counter.host, unit, counter.hostW);
    const unitT = runPosition(counter, unit.center);
    let start;
    let end;
    if (onSlab && nearCounter(counter, unit, 0.02)) {
      // On (or abutting) this slab: between the two rims.
      if (onSlab.start >= iv.end) [start, end] = [iv.end, onSlab.start];
      else if (onSlab.end <= iv.start) [start, end] = [onSlab.end, iv.start];
      else continue;
    } else {
      // Abutting the slab end, the whole stretch to it IS between sink and
      // range; anywhere else, only the prep area beside the sink is.
      const reach = nearCounter(counter, unit, RANGE_ABUTS_M)
        ? Infinity
        : PREP_AREA_M;
      if (unitT > (iv.start + iv.end) / 2)
        [start, end] = [iv.end, Math.min(counter.hostW, iv.end + reach)];
      else [start, end] = [Math.max(0, iv.start - reach), iv.start];
    }
    if (end > start) out.push({ start, end });
  }
  return out;
}

/**
 * Run stretches too low for a prop `height` tall standing in the depth band
 * [zMin, zMax] of the counter. A band at the front is not under an upper at
 * all (an upper is ~12 in deep over a ~25 in counter), so this is depth-aware.
 */
function headroomBlocks(counter, height, zMin, zMax) {
  return counter.overhead
    .filter(
      (o) =>
        o.clear < height + HEADROOM_CLEARANCE_M &&
        o.zMax > zMin &&
        o.zMin < zMax
    )
    .map((o) => ({
      start: o.start - SURFACE_VOID_PAD_M,
      end: o.end + SURFACE_VOID_PAD_M,
    }));
}

/**
 * Stretches of a counter's run where a prop of `type`, seated at `depth`,
 * would stand within GROUP_SPACING_M (in PLAN) of something of another
 * grouping (`KITCHEN_GROUPING`) — a prop already out, or the stove. Stagers
 * set groupings apart and never line them up, so a new prop keeps bare
 * counter between itself and the last grouping, on its own slab or a slab
 * continuing it. Something off to the side (across the counter, or on the
 * other leg of an L) needs correspondingly less run between them.
 *
 * @param {Object} counter  from `readKitchen`
 * @param {Object[]} others  props out and the room's cooking units
 * @returns {Array<{start, end}>} run intervals, clamped to the counter
 */
function groupingSpacing(counter, others, type, depth) {
  const own = KITCHEN_GROUPING[type];
  const ofOtherGroupings = (others || []).filter((o) => {
    const theirs =
      KITCHEN_GROUPING[o?.type] || KITCHEN_GROUPING[objectTypeOf(o)];
    return theirs && theirs !== own;
  });
  return nearBlocks(counter, ofOtherGroupings, type, depth, GROUP_SPACING_M);
}

/**
 * Stretches of a counter's run where a prop of `type`, seated at `depth`,
 * would stand within `distM` (in PLAN) of any of `objects`. Distance is taken
 * from the depth band the prop will occupy, so something off to the side
 * needs correspondingly less run between them.
 *
 * @returns {Array<{start, end}>} run intervals, clamped to the counter
 */
function nearBlocks(counter, objects, type, depth, distM) {
  const host = counter.host;
  const dz = stagingPropDimsM(type)?.z || 0;
  const lz = depthOffset(depth, host.dimensions.z, dz);
  const out = [];
  for (const o of objects || []) {
    if (!o?.center || !o?.dimensions) continue;
    const { x: w, z: d } = planDims(o);
    const corners = footprintFor(o.center, w, d, o.rotation?.y || 0);
    if (!corners) continue;
    const ts = corners.map((p) => objectLocalX(host, p) + counter.hostW / 2);
    const zs = corners.map((p) => objectLocalZ(host, p));
    const across = Math.max(
      0,
      Math.min(...zs) - (lz + dz / 2),
      lz - dz / 2 - Math.max(...zs)
    );
    if (across >= distM) continue;
    const along = Math.sqrt(distM ** 2 - across ** 2);
    // Clamped to the run: a stretch past the slab's end is not a blocker,
    // and the free-run pass reads one as counter.
    const start = Math.max(0, Math.min(...ts) - along);
    const end = Math.min(counter.hostW, Math.max(...ts) + along);
    if (end > start) out.push({ start, end });
  }
  return out;
}

module.exports = {
  isKitchenVoid,
  prepZone,
  headroomBlocks,
  groupingSpacing,
  nearBlocks,
  PREP_AREA_M,
};
